import { useApiClient } from '~/api-client';
import { FederatedSearchResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';

export function useEventFederatedQueryIds(props: { eventId?: number }) {
    const [federatedQueryIds, setFederatedQueryIds] = useState<string[]>([]);
    const apiClient = useApiClient();
    const { federatedSearchIdAwareStreamsEnabled } = useEnabledFeatures();

    const query = useQuery({
        queryKey: ['fed-search-queries-for-event', props.eventId ?? 0],
        queryFn() {
            if (!props.eventId) return;
            return apiClient.request<FederatedSearchResource.GetFederatedSearchQueries>({
                method: 'POST',
                url: 'federated_search/get_federated_search_queries/',
                data: {
                    entityType: 'CAD_AGENCY_EVENT',
                    entityId: props.eventId,
                },
            });
        },
        enabled: !!props.eventId,
    });

    const queryFederatedQueryIds =
        query.data?.data.data?.federatedSearchQueryBundle.federatedQueryIds;

    useEffect(() => {
        if (queryFederatedQueryIds) {
            setFederatedQueryIds(queryFederatedQueryIds);
        }
    }, [queryFederatedQueryIds]);

    useIntegratedStreams({
        enabled: !!props.eventId,
        streamNames: [
            federatedSearchIdAwareStreamsEnabled
                ? (`eventFederatedSearchQueryBundles|${props.eventId}` as 'eventFederatedSearchQueryBundles')
                : 'eventFederatedSearchQueryBundles',
        ],
        query,
        onMessage(message) {
            if (message.payload.federatedQueryIds) {
                setFederatedQueryIds((curr) =>
                    uniq(curr.concat(message.payload.federatedQueryIds))
                );
            }
        },
    });

    return federatedQueryIds;
}
